import { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { WMSFetch } from '../../utils/common-api-calls';
import logError from '../../../utils/errorUtil';
import config from '../../../utils/config';

export default function GoBackModal({
    open,
    setOpen,
    inventoryData,
    locationId,
    currentUser,
    setNotification,
    setNotificationMessage,
    setLocations,
    setLocationId,
    setInventoryNumber,
    locationName,
}) {
    const ORIGIN = config.origin;
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [confirmAndPrintLoading, setConfirmAndPrintLoading] = useState(false);
    const confirmButtonRef = useRef(null);

    useEffect(() => {
        if (open && confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (!confirmAndPrintLoading) {
                    handleTransfer(true);
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, confirmAndPrintLoading, confirmLoading]);

    const stopLoading = () => {
        setConfirmAndPrintLoading(false);
        setConfirmLoading(false);
    };

    const handleTransfer = async (printInventory = false) => {
        if (printInventory) {
            setConfirmAndPrintLoading(true);
        } else {
            setConfirmLoading(true);
        }
        // if the user clicks on confirm it needs to update the location in SF and print out the new print tag
        const updateObj = {
            inventoryLineId: inventoryData.inventoryLineId,
            inventoryLocationId: locationId,
        };
        const response = await WMSFetch('wms/go-back/update-inventory-location', updateObj, currentUser);

        if (!response.success) {
            setNotification(true);
            setNotificationMessage({
                status: 'error',
                title: `Failed to update Sales Force.`,
                message: `Failed to update inventory line. If the problem persists, please contact your manager.`,
            });
            stopLoading();
            return;
        }

        if (printInventory) await printInventoryLabel();

        setLocations('');
        setLocationId('');
        setInventoryNumber('');
        setOpen(false);
        stopLoading();

        setNotification(true);
        setNotificationMessage({
            status: 'success',
            title: `Updated Sales Force.`,
            message: `Updated Inventory Location. `,
        });
    };

    const printInventoryLabel = async () => {
        // print the barcode
        const inventory = {
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
            inventory: [
                {
                    Id: inventoryData.inventoryLineId,
                    Name: inventoryData.inventoryLineName,
                },
            ],
        };

        fetch(`${ORIGIN}/wms/inventory/print-inventory`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(inventory),
        })
            .then((res) => res.blob())
            .then((data) => {
                // render the blob as pdf in a new tab
                const file = new Blob([data], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);

                fileURL && window.open(fileURL);
            })
            .catch((err) => {
                logError(err, 'handlePrintIndividual');
            });
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as='div' className='relative z-10' onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter='ease-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in duration-200'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
                </Transition.Child>

                <div className='overflow-y-auto fixed inset-0 z-10'>
                    <div className='flex justify-center items-end p-4 min-h-full text-center sm:items-center sm:p-0'>
                        <Transition.Child
                            as={Fragment}
                            enter='ease-out duration-300'
                            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                            enterTo='opacity-100 translate-y-0 sm:scale-100'
                            leave='ease-in duration-200'
                            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                        >
                            <Dialog.Panel className='overflow-hidden relative text-center bg-white rounded-lg shadow-xl transition-all transform sm:my-8 sm:w-full sm:max-w-lg'>
                                {/* Full-width banner */}
                                <div className='px-6 py-3 text-white bg-blue-primary'>
                                    <Dialog.Title as='h2' className='text-lg font-semibold'>
                                        Transfer INV to Location: <span className='underline'>{locationName}</span>
                                    </Dialog.Title>
                                </div>
                                {/* Body with padding */}
                                <div className='px-6 py-6'>
                                    <div className='mb-6 space-y-1'>
                                        <p className='text-gray-600'>{inventoryData.inventoryLineName}</p>
                                        <p className='text-gray-600'>{inventoryData.partName}</p>
                                        <p className='text-gray-600'>Quantity Available:</p>
                                        <p className='text-5xl font-bold text-gray-900'>{inventoryData.inventoryLineQty}</p>
                                    </div>

                                    {/* Buttons */}
                                    <div className='flex gap-4 justify-between'>
                                        <button
                                            type='button'
                                            className='flex-1 justify-center py-2 w-full text-sm font-semibold text-white bg-red-600 rounded-md border border-gray-300 shadow-sm nline-flex hover:bg-red-500 sm:ml-3 sm:w-auto'
                                            onClick={() => setOpen(false)}
                                        >
                                            Close
                                        </button>
                                        {confirmLoading ? (
                                            <button
                                                type='button'
                                                className='flex-1 py-2 text-sm text-white rounded-md border bg-blue-primary hover:bg-blue-primary hover:text-white border-blue-primary'
                                                ref={confirmButtonRef}
                                            >
                                                <div className='flex gap-2 justify-center items-center'>
                                                    <div className='w-4 h-4 rounded-full border-b-2 border-white animate-spin border-e-2' />
                                                    <span>Loading</span>
                                                </div>
                                            </button>
                                        ) : (
                                            <button
                                                type='button'
                                                className='flex-1 py-2 text-sm text-white rounded-md border bg-blue-primary hover:bg-blue-primary hover:text-white border-blue-primary'
                                                onClick={() => handleTransfer()}
                                                ref={confirmButtonRef}
                                                autoFocus
                                            >
                                                Confirm
                                            </button>
                                        )}
                                        {confirmAndPrintLoading ? (
                                            <button
                                                type='button'
                                                className='flex-1 py-2 text-sm text-white rounded-md border bg-blue-primary hover:bg-blue-primary hover:text-white border-blue-primary'
                                                ref={confirmButtonRef}
                                            >
                                                <div className='flex gap-2 justify-center items-center'>
                                                    <div className='w-4 h-4 rounded-full border-b-2 border-white animate-spin border-e-2' />
                                                    <span>Loading</span>
                                                </div>
                                            </button>
                                        ) : (
                                            <button
                                                type='button'
                                                className='flex-1 py-2 text-sm text-white rounded-md border bg-blue-primary hover:bg-blue-primary hover:text-white border-blue-primary'
                                                onClick={() => handleTransfer(true)}
                                                ref={confirmButtonRef}
                                                autoFocus
                                            >
                                                Confirm and Print
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
